<template>
  <header class="topnav">
    <div class="topnav-inner">
      <router-link :to="{ name: 'dashboard' }" class="topnav-brand" @click="closeMenu">
        <span class="topnav-brand-mark">R2</span>
        <span>Reparaturtool</span>
      </router-link>

      <button
        class="topnav-toggle"
        :class="{ 'is-open': menuOpen }"
        type="button"
        :aria-expanded="menuOpen"
        aria-label="Menü öffnen"
        @click="menuOpen = !menuOpen"
      >
        <span class="topnav-toggle-bar"></span>
        <span class="topnav-toggle-bar"></span>
        <span class="topnav-toggle-bar"></span>
      </button>

      <div class="topnav-collapse" :class="{ 'is-open': menuOpen }">
        <nav>
          <ul class="topnav-menu">
            <li
              v-for="item in menu"
              :key="item.label"
              :class="{ 'has-submenu': !!item.children }"
            >
              <template v-if="item.children">
                <a href="#" @click.prevent>{{ item.label }}</a>
                <ul class="topnav-submenu">
                  <li v-for="child in item.children" :key="child.label">
                    <router-link :to="child.to" @click="closeMenu">{{ child.label }}</router-link>
                  </li>
                </ul>
              </template>
              <router-link v-else-if="item.to" :to="item.to" @click="closeMenu">{{ item.label }}</router-link>
              <a v-else href="#" @click.prevent>{{ item.label }}</a>
            </li>
          </ul>
        </nav>

        <div class="topnav-spacer"></div>

        <div class="topnav-settings" :class="{ 'is-open': settingsOpen }">
          <button
            type="button"
            class="topnav-settings-btn"
            :class="{ 'is-open': settingsOpen }"
            aria-label="Einstellungen"
            title="Einstellungen"
            :aria-expanded="settingsOpen"
            @click="settingsOpen = !settingsOpen"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg" width="20" height="20"
              viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
            <span class="topnav-settings-label">Einstellungen</span>
          </button>
          <ul class="topnav-dropdown" :class="{ 'is-open': settingsOpen }">
            <li><a href="#" @click.prevent="onOpenPrinter">Drucker</a></li>
          </ul>
        </div>

        <div class="topnav-user" v-if="user">
          <span class="topnav-user-avatar">{{ initials }}</span>
          <span class="topnav-user-name">{{ displayName }}</span>
        </div>
        <button class="topnav-logout" @click="onLogout">Abmelden</button>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { getCurrentUser, logout, type CurrentUser } from '../services/auth';
import { getPeppiPrinterUrl } from '../services/me';
import type { RouteLocationRaw } from 'vue-router';

interface MenuChild {
  label: string;
  to:    RouteLocationRaw;
}
interface MenuItem {
  label:     string;
  to?:       RouteLocationRaw;
  children?: MenuChild[];
}

const user         = ref<CurrentUser | null>(null);
const menuOpen     = ref(false);
const settingsOpen = ref(false);

const menu: MenuItem[] = [
  {
    label:    'Stammdaten',
    children: [
      { label: 'Masterartikel',  to: { name: 'masterarticles' } },
      { label: 'Servicepartner', to: { name: 'servicepartner' } },
      { label: 'Fehlerbilder',   to: { name: 'errorpatterns' } },
    ],
  },
  { label: 'Assona24H',     to: { name: 'assona24h' } },
  { label: 'Gerätelager',   to: { name: 'stockdevices' } },
  // Eigener Punkt und kein Untermenü: die Übersicht führt beide Richtungen
  // (hinaus und zurück) und ist damit die Stelle, an der eine RL-Nummer
  // nachgeschlagen wird - dafür geht man nicht erst durch „Weiteres".
  { label: 'Lieferscheine', to: { name: 'deliveryslips' } },
];

const displayName = computed(() => {
  const u = user.value;
  if (!u) return '';
  const full = [u.vn, u.nn].filter(Boolean).join(' ').trim();
  return full || u.userName;
});

const initials = computed(() => {
  const u = user.value;
  if (!u) return '';
  const a = (u.vn ?? '').trim();
  const b = (u.nn ?? '').trim();
  if (a || b) return ((a[0] ?? '') + (b[0] ?? '')).toUpperCase() || u.userName.slice(0, 2).toUpperCase();
  return u.userName.slice(0, 2).toUpperCase();
});

onMounted(async () => {
  try {
    user.value = await getCurrentUser();
  } catch {
    // Profil-Load fehlgeschlagen — topnav zeigt dann nur Brand+Menü ohne User.
  }
});

function closeMenu(): void {
  menuOpen.value     = false;
  settingsOpen.value = false;
}

/**
 * Öffnet die Drucker-Verwaltung im Solo in einem neuen Tab. Das Fenster wird
 * synchron im Klick geöffnet (sonst greift der Popup-Blocker) und erst danach
 * mit der vom Backend gelieferten token-authentifizierten URL befüllt.
 */
async function onOpenPrinter(): Promise<void> {
  settingsOpen.value = false;
  const win = window.open('', '_blank');
  try {
    const url = await getPeppiPrinterUrl();
    if (win) win.location.href = url;
    else window.open(url, '_blank');
  } catch (e) {
    win?.close();
    window.alert(e instanceof Error ? e.message : 'Der Drucker-Link konnte nicht geladen werden.');
  }
}

function onLogout(): void {
  logout('sso');
}
</script>

<style scoped>
.topnav-settings {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.topnav-settings-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: inherit;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.2s ease;
}

.topnav-settings-btn:hover {
  background: rgba(0, 0, 0, 0.06);
}

.topnav-settings-btn.is-open {
  background: rgba(0, 0, 0, 0.08);
  transform: rotate(45deg);
}

/* Auf Desktop ist der Button nur das Zahnrad — das Textlabel bleibt verborgen. */
.topnav-settings-label {
  display: none;
}

.topnav-dropdown {
  position: absolute;
  right: 0;
  top: 100%;
  margin-top: 6px;
  list-style: none;
  padding: 6px 0;
  background: #fff;
  border: 1px solid #d4d4d8;
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  min-width: 160px;
  z-index: 1000;
  display: none;
}

.topnav-dropdown.is-open {
  display: block;
}

.topnav-dropdown li a {
  display: block;
  padding: 8px 16px;
  color: #1d1d1f;
  text-decoration: none;
  white-space: nowrap;
}

.topnav-dropdown li a:hover {
  background: rgba(0, 0, 0, 0.05);
}

/* Mobil: Im aufgeklappten Hamburger-Panel ist „Einstellungen" ein normaler
   Menüpunkt (volle Breite, Zahnrad + Text) mit „Drucker" als statischem
   Untermenü — analog zu „Stammdaten". Kein absolut positioniertes Dropdown
   mehr, das sonst irgendwo über dem Panel landet. */
@media (max-width: 768px) {
  .topnav-settings {
    display: block;
    width: 100%;
  }

  .topnav-settings-btn {
    width: 100%;
    height: auto;
    justify-content: flex-start;
    gap: 10px;
    padding: 12px 14px;
    border-radius: 8px;
    color: var(--text);
    font: inherit;
    font-size: 16px;
    font-weight: 500;
  }

  /* Drehung des Zahnrads im Menüfluss vermeiden (würde den Text mitkippen). */
  .topnav-settings-btn.is-open {
    transform: none;
  }

  .topnav-settings-label {
    display: inline;
  }

  .topnav-dropdown {
    position: static;
    display: block;
    margin: 0 0 4px;
    padding: 0 0 4px 12px;
    border: none;
    box-shadow: none;
    min-width: 0;
    background: transparent;
  }

  .topnav-dropdown li a {
    padding: 10px 14px;
    border-radius: 6px;
    font-size: 16px;
  }
}
</style>
